import React, {
    Dispatch,
    SetStateAction,
    useCallback,
    useMemo,
    useState,
} from 'react';
import {PDFIcon} from 'assets';
import {
    Col,
    Container as ContainerBS,
    Row,
    Spinner,
    Stack,
} from 'react-bootstrap';
import {CancelButton} from 'shared/components/CancelButton';
import {CBCButton, Loader} from 'shared/helpers';
import styled from 'styled-components';
import {useJobSubmitHandler} from 'components/customer/Job/helpers/useJobSubmitHandler';
import {mobile} from 'shared/Breakpoints';
import {PartialJob} from 'shared/types/PartialJob';
import {submitJobButtonText} from 'shared/components/Header/helper/submitJobButtonText';
import {useJobContext} from 'contexts/JobContext';
import {useJobPDFVerificationLink} from 'components/customer/Job/helpers/useJobPDFVerificationLink';
import {useAppContext} from 'contexts/ApplicationContext';
import {useResetJob} from 'components/customer/Confirmation/helpers/useResetJob';
import {useJobVariations} from 'shared/components/Header/helper/useJobVariations';
import {usePaymentSettings} from 'components/customer/Confirmation/helpers/usePaymentSettings';
import {useAppSelector} from 'store/customer';
import {
    selectAmountPaid,
    selectHasCustomerOverpaid,
    selectIsLoadingMisc,
} from 'components/customer/Confirmation/store/confirmationSlice';
import {useDisableCheckout} from 'components/customer/Confirmation/helpers/selectDisableCheckout';
import {ConfirmationKey} from 'components/customer/Confirmation/ConfirmationTabTitle';

interface FooterProps {
    currentTab: ConfirmationKey;
    setCurrentTab: Dispatch<SetStateAction<ConfirmationKey>>;
    onCheckout?: () => void;
}

export const Footer = ({
    currentTab,
    setCurrentTab,
    onCheckout,
}: FooterProps) => {
    const {userProfile} = useAppContext();
    const captureDeliveryInfo =
        userProfile &&
        userProfile.jobConfirmationEnabled &&
        !userProfile.capturePickupFreightAtJobCreation;
    const {job} = useJobContext() as PartialJob;
    const [confirmed, setConfirmed] = useState(false);
    const {handleJobSubmission, loader} = useJobSubmitHandler();
    const {noVariations, extraVariation} = useJobVariations();
    const {pdfLink} = useJobPDFVerificationLink();
    const {handleResetJob} = useResetJob();
    const {paymentSettings} = usePaymentSettings();
    const overPaid = useAppSelector(selectHasCustomerOverpaid);
    const isLoadingMiscData = useAppSelector(selectIsLoadingMisc);
    const amountPaid = useAppSelector(selectAmountPaid);
    const disableCheckout = useDisableCheckout();

    const handleBackButton = useCallback(() => {
        window.location.href = '/v2';
        return true;
    }, []);

    const handleCheckoutButton = useCallback(() => {
        if (onCheckout) {
            onCheckout();
        } else {
            setCurrentTab(ConfirmationKey.CHECKOUT);
        }
    }, [setCurrentTab, onCheckout]);

    const handleFreightButton = useCallback(() => {
        setCurrentTab(ConfirmationKey.FREIGHT);
    }, [setCurrentTab]);

    const showBackToFreight =
        captureDeliveryInfo && currentTab == ConfirmationKey.CHECKOUT;

    const submitButtonText = useMemo(
        () =>
            submitJobButtonText({
                job,
                noVariations,
                extraVariation,
                paymentGatewaysEnabled:
                    paymentSettings?.allowPayOnAccount ||
                    paymentSettings?.enableCardPayments ||
                    paymentSettings?.enableInvoicePayments,
                jobConfirmationEnabled: userProfile?.jobConfirmationEnabled,
                confirmationPage: true,
                overPaid,
            }),
        [
            job,
            noVariations,
            extraVariation,
            userProfile,
            paymentSettings,
            overPaid,
        ]
    );

    const handleConfirmation = useCallback(() => {
        setConfirmed((confirmed) => !confirmed);
    }, []);

    const handleSubmitButton = useCallback(
        (event: React.MouseEvent) => {
            void handleJobSubmission(event);
        },
        [handleJobSubmission]
    );

    return (
        <div className="nav-and-control">
            <Loader loader={loader} />
            <Container>
                {currentTab == ConfirmationKey.CHECKOUT ? (
                    <Row>
                        <Col>
                            <PDFConfirmation>
                                <input
                                    type="checkbox"
                                    checked={confirmed}
                                    id="confirmation"
                                    name="confirmation"
                                    onChange={handleConfirmation}
                                />
                                <label htmlFor="confirmation">
                                    I have reviewed the{' '}
                                    <a
                                        href={pdfLink.url}
                                        target="_blank"
                                        rel="noreferrer">
                                        {pdfLink.name}
                                        <img src={PDFIcon} alt="PDF icon" />
                                    </a>{' '}
                                    and confirm all information is correct
                                </label>
                            </PDFConfirmation>
                        </Col>
                    </Row>
                ) : null}
                <ControlButtons className="control-buttons">
                    <Col xs={6}>
                        {isLoadingMiscData ? (
                            <Stack className="align-items-center">
                                <Spinner />
                            </Stack>
                        ) : showBackToFreight ? (
                            <CBCButton
                                onClick={handleFreightButton}
                                type="button"
                                iconName="Button-Previous.svg"
                                className="job-button button-blue">
                                Pickup / Freight
                            </CBCButton>
                        ) : (amountPaid ?? 0) > 0 ? (
                            <CancelButton
                                iconName="Home-gray.svg"
                                noDialog={true}
                                label="Return home"
                                beforeRedirect={handleBackButton}
                            />
                        ) : (
                            <CancelButton
                                noDialog={true}
                                label="Back to Ordering"
                                beforeRedirect={handleResetJob}
                            />
                        )}
                    </Col>
                    <Col xs={6}>
                        {currentTab == ConfirmationKey.CHECKOUT ? (
                            <CBCButton
                                onClick={handleSubmitButton}
                                disabled={
                                    !confirmed ||
                                    job.totalProductCount == 0 ||
                                    isLoadingMiscData
                                }
                                type="submit"
                                iconName="Button-Tick.svg"
                                className="job-button button-blue">
                                {submitButtonText}
                            </CBCButton>
                        ) : (
                            <CBCButton
                                onClick={handleCheckoutButton}
                                disabled={disableCheckout}
                                type="button"
                                iconName="Submit-white.svg"
                                className="job-button button-blue">
                                Checkout
                            </CBCButton>
                        )}
                    </Col>
                </ControlButtons>
            </Container>
        </div>
    );
};

const Container = styled(ContainerBS)`
    padding: 30px 0;

    ${mobile`
        padding: 15px !important;    
    `}
`;

const PDFConfirmation = styled.div`
    text-align: center;

    > label {
        font-size: 0.9em;
        color: #595959;
        font-weight: 400;
        cursor: pointer;
        user-select: none;
        padding-bottom: 10px;

        > a > img {
            width: 25px;
        }
    }

    > input {
        vertical-align: -2px;
        margin-right: 5px;
    }

    ${mobile`
        display: flex;
        justify-content: space-between;
        align-items: center;

        > input {
            margin-right: initial;
        }
    `}
`;

const ControlButtons = styled(Row)`
    padding: 0 !important;
`;
